'use client';
import { ApplyCtaLink } from '@hyphengroup/cms-components/components/ApplyCta';
import { hasPdpModalContent } from '@hyphengroup/cms-components/components/ProductPage/hasPdpModalContent';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '@hyphengroup/cms-components/utils/analytics/sendAnalytics';
import { getApplyButtonLabel } from '@hyphengroup/cms-components/utils/helpers/router';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import React, { useCallback, useEffect, useMemo } from 'react';
import { tr } from '../../helpers';
import {
  getProductRewardBadgeCopy,
  getProductSharedAttributeData,
} from '../../helpers/productRecommendation';
import { pushDataLayer } from '../../pushDataLayer';
import { buildCardItems, getCardKey } from './helpers';
import { MultiProductBlockTranslations } from './i18n';
import { MultiProductBlockCard } from './MultiProductBlockCard';
import { MultiProductBlockPlacement, MultiProductBlockProps } from './types';
import { CmsProduct } from '$/(cms)/_modules/cmsService/types';
import Button from '$/components/Button';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';
import { FeLink } from '$/components/FeLink';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';

export const MultiProductBlock: React.FC<MultiProductBlockProps> = ({
  id,
  pageType,
  vertical,
  slug,
  title,
  products,
  see_all_copy,
  see_all_link,
  target,
  shared_product_attribute,
  placement = 'inline',
}) => {
  const context = useClientContext();
  const { lang } = context;
  const staticText = getClientTranslations(
    MultiProductBlockTranslations,
    context,
  );
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `MultiProductBlock_${id}`,
  });

  const items = useMemo(() => buildCardItems(products), [products]);

  useEffect(() => {
    if (items.length > 0 || pageType !== 'article') {
      return;
    }

    console.warn(
      `[MultiProductBlock:${id}] No published products found. Widget will not render.`,
    );
  }, [id, items.length, pageType]);

  const getSharedAttributeData = useCallback(
    (product: CmsProduct) => {
      return getProductSharedAttributeData(
        product,
        shared_product_attribute?.name,
      );
    },
    [shared_product_attribute?.name],
  );

  const getBadgeCopy = useCallback(
    (product: CmsProduct, overrideBadge?: string) => {
      return getProductRewardBadgeCopy({
        product,
        staticText,
        vertical,
        overrideBadge,
      });
    },
    [staticText, vertical],
  );

  const renderApplyButton = useCallback(
    (product: CmsProduct, index: number, fullWidth = true) => {
      // <ApplyCtaLink> self-hides when the CTA url does not resolve, so no
      // visibility gate is needed here (TI / GE / TI-article products
      // synthesize a url even when redirected_url is empty).
      const applyLabel = getApplyButtonLabel(
        tr('apply_now', lang),
        undefined,
        product.call_to_action,
      );

      return (
        <ApplyCtaLink
          product={product}
          target="_blank"
          rel="noopener noreferrer"
          className={fullWidth ? 'w-full' : undefined}
        >
          <Button
            size="small"
            variant="outline"
            styleType="cta"
            className={fullWidth ? 'w-full' : undefined}
            onClick={() => {
              sessionAttributionTracker.recordApplyClick();
              sendProductAnalytics({
                action: UserActionEnum.CLICK,
                componentTitle: 'Product Card/CTA_Apply Now',
                componentType: ComponentTypeEnum.PRODUCT_CARD,
                event: 'product_click_apply',
                index: index.toString(),
                url: slug,
                product,
                pushDataLayer,
              });
            }}
          >
            {applyLabel}
          </Button>
        </ApplyCtaLink>
      );
    },
    [lang, slug],
  );

  const renderViewDetailsButton = useCallback(
    (product: CmsProduct, index: number, fullWidth = true) => {
      // Hide "View Details" when opening the PDP modal would show a near-empty
      // body (name + logo + Apply only) — see hasPdpModalContent.
      if (!product.url_alias || !hasPdpModalContent(product)) {
        return null;
      }

      return (
        <Button
          size="small"
          variant="outline"
          styleType="neutral-light-and-blue"
          className={fullWidth ? 'w-full' : undefined}
          onClick={() => {
            sendProductAnalytics({
              action: UserActionEnum.CLICK,
              componentTitle: 'Product Card/Footer_View Details',
              componentType: ComponentTypeEnum.PRODUCT_CARD,
              event: 'product_interaction',
              index: index.toString(),
              url: slug,
              product,
              pushDataLayer,
            });

            openProductDetail(product, null);
          }}
        >
          {tr('spotlight_details', lang)}
        </Button>
      );
    },
    [lang, openProductDetail, slug],
  );

  const seeAllText = useMemo(() => {
    if (!see_all_copy?.trim()) {
      return staticText.seeAll;
    }

    return see_all_copy.trim();
  }, [see_all_copy, staticText.seeAll]);

  const renderSeeAllLink = useCallback(
    (className?: string) => {
      if (!see_all_link?.trim() || !seeAllText) {
        return null;
      }

      return (
        <FeLink
          href={see_all_link}
          target={target === 'NEW_TAB' ? '_blank' : '_self'}
          rel={target === 'NEW_TAB' ? 'noopener noreferrer' : undefined}
          className={classNames(
            'mhg-alpha-typography-button-small inline-flex items-center gap-1 underline text-text/link',
            className,
          )}
        >
          {seeAllText}
          <MaterialIcon icon="arrow_right_alt" px={18} className="size-4" />
        </FeLink>
      );
    },
    [seeAllText, see_all_link, target],
  );

  const content = (
    <section className="w-full" data-component="multi-product-block">
      <div className="mb-spacing/gap/y flex items-start justify-between gap-spacing/gap/soft-12px">
        <h2 className="mhg-alpha-typography-display-1 text-text/primary">
          {title}
        </h2>
        <div className="hidden md:block lg:hidden">
          {renderSeeAllLink('whitespace-nowrap')}
        </div>
      </div>

      <div className="rounded-radius/card border border-borders/default bg-fill/base md:bg-fill/card">
        <div className="flex flex-col md:grid md:grid-cols-3 lg:flex lg:flex-col">
          {items.map((item, index) => (
            <div
              key={getCardKey(item, index)}
              className={classNames(
                'border-b border-borders/default p-spacing/padding/soft-12px md:border-b-0 md:border-r lg:border-b lg:border-r-0',
                {
                  'border-b-0 md:border-r-0 lg:border-b-0':
                    index === items.length - 1,
                },
              )}
            >
              <MultiProductBlockCard
                item={item}
                index={index}
                getBadgeCopy={getBadgeCopy}
                getSharedAttributeData={getSharedAttributeData}
                renderApplyButton={renderApplyButton}
                renderViewDetailsButton={renderViewDetailsButton}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-spacing/gap/y md:hidden lg:block">
        {renderSeeAllLink()}
      </div>
      {productDetailModal}
    </section>
  );

  if (pageType !== 'article' || items.length === 0) {
    return null;
  }

  const placementClassMap: Record<MultiProductBlockPlacement, string> = {
    inline: 'mb-spacing/gap/y lg:hidden',
    'right-rail': 'mb-spacing/gap/y hidden lg:block',
  };

  return <div className={placementClassMap[placement]}>{content}</div>;
};
